<script setup>
import dayjs from 'dayjs'
import { ref, computed, onMounted, toRef } from 'vue'
import { Dialog } from 'quasar'
import { getI18n } from '@/plugins/i18n'
import { stringToColor, textColor } from '@/lib/utils'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { usePlanningStore } from '@/stores/planning.store'

import { useStepper } from '@/composables/stepper'
import { useNotify } from '@/composables/notify'
import { useIsUpdated } from '@/composables/object'
import { useDetectDevice } from '@/composables/device'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useTagDraft } from '@/composables/tag'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import FormSectionTitle from '@/components/shared/FormSectionTitle.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import ListingSelect from '@/components/shared/ListingSelect.vue'
import CompanyCombobox from '@/components/shared/CompanyCombobox.vue'
import ContactSelect from '@/components/shared/ContactSelect.vue'
import ContactReferenceInput from '@/components/shared/ContactReferenceInput.vue'
import CurrencySelect from '@/components/shared/CurrencySelect.vue'
import DateTimeRangeStep from '@/components/shared/DateTimeRangeStep.vue'
import DeleteOptionsDialog from '@/components/shared/DeleteOptionsDialog.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import DocumentTermTemplateSelect from '@/components/accounting/DocumentTermTemplateSelect.vue'
import CreateModeToggle from '@/components/shared/CreateModeToggle.vue'
import PrintTemplateSelect from '@/components/accounting/PrintTemplateSelect.vue'
import DocumentLanguageSelect from '@/components/shared/DocumentLanguageSelect.vue'
import ExchangeRatePanel from '@/components/shared/ExchangeRatePanel.vue'
import TagCreateDialog from '@/components/shared/TagCreateDialog.vue'

import { useQuoteDeleteOptions } from '@/composables/delete-options'
import { useCompanyResolve, useContactResolve, useContactReferences, useListingCompany } from '@/composables/company-resolve'
import { pickExchangeRate, referenceCurrencyHint, useExchangeRateEdit } from '@/composables/exchange-rate'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  changes: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  initialQuickCreate: {
    type: Boolean,
    required: false,
    default: false,
  },
  initialContactReferences: {
    type: Object,
    required: false,
    default: null,
  },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { step, nextStep, prevStep, markAttempted, wasAttempted } = useStepper()
const { notifySuccess, notifyError, notifyWarning } = useNotify()

const accountingStore = useAccountingStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const planningStore = usePlanningStore()
const { resolveCompany, rollbackCompany } = useCompanyResolve()
const { resolveContact, notifyContactResolved } = useContactResolve()
const { contactReferences, contactReferencesChanged, loadContactReferences } = useContactReferences()
if (props.initialContactReferences) loadContactReferences({ ...contactReferences.value, ...props.initialContactReferences })
const isUpdated = computed(() => isItemUpdated.value || contactReferencesChanged.value)

const editItem = ref({
  start: dayjs(`${dayjs().format('YYYY-MM-DD')}T${webSiteStore.getDefaultStartTime()}`).toISOString(),
  end: dayjs(`${dayjs().format('YYYY-MM-DD')}T${webSiteStore.getDefaultEndTime()}`).toISOString(),
  is_estimate: true,
  date: dayjs().format('YYYY-MM-DD'),
  currency_ref: webSiteStore.getDefaultCurrency().id,
  schedules: [{ start_time: webSiteStore.getDefaultStartTime(), end_time: webSiteStore.getDefaultEndTime() }],
  ...(props.modelValue.id ? {} : { print_template: webSiteStore.getDefaultQuotePrintTemplate(), language: portalStore.selectedLanguage }),
  ...props.modelValue,
  tags: props.modelValue?.tag_ids || [],
})
const { isUpdated: isItemUpdated, setOriginal } = useIsUpdated(editItem)

const basicInfoHasError = computed(() => !editItem.value.name)

const quickCreate = ref(props.initialQuickCreate && !props.modelValue.id)
const quickStep = ref(1)

const availableBudgetDocumentTags = ref([])
const onBudgetDocumentTagFilter = (val, update) => {
  update(() => {
    availableBudgetDocumentTags.value = val
      ? accountingStore.getLocalizedBudgetTags().filter(t => t.name?.toLowerCase().includes(val.toLowerCase()))
      : accountingStore.getLocalizedBudgetTags()
  })
}
const { tagDraft, openTagDraft: onCreateBudgetDocumentTag, confirmTagDraftAsync } = useTagDraft(accountingStore.createBudgetTagAsync)

const { onCurrencyChange, onExchangeRateChange, confirmRepriceRowsAsync } = useExchangeRateEdit(editItem, toRef(props, 'modelValue'))

const onUpdate = async() => {
  const repriceRows = props.modelValue.rows_count ? await confirmRepriceRowsAsync() : false
  if (repriceRows === null) return
  const typedCompany = editItem.value.company
  const typedContact = editItem.value.contact
  let createdCompanyId = null
  let resolvedContact = null
  let documentSaved = false
  try {
    const original = props.modelValue
    const { id, createdId } = await resolveCompany(typedCompany)
    createdCompanyId = createdId
    editItem.value.company = id
    resolvedContact = await resolveContact(id, typedContact, contactReferences.value)
    editItem.value.contact = resolvedContact.id
    const saved = await accountingStore.upsertBudgetDocumentAsync({ ...editItem.value, reprice_rows: repriceRows })
    documentSaved = true

    notifySuccess(editItem.value.id ? i18n.t('info.updatedSuccessfully') : i18n.t('info.createdSuccessfully'))
    notifyContactResolved(resolvedContact.result)

    if (original?.id && original.projects?.length > 0) {
      const origListingId = typeof original.listing === 'object' ? original.listing?.id : original.listing
      const newListingId = typeof editItem.value.listing === 'object' ? editItem.value.listing?.id : editItem.value.listing
      const fieldsChanged =
        editItem.value.start !== original.start ||
        editItem.value.end !== original.end ||
        editItem.value.currency_ref !== original.currency_ref ||
        newListingId !== origListingId
      if (fieldsChanged) notifyWarning(i18n.t('warning.budgetDocumentLinkedProjectFieldsChanged'))
    }

    editItem.value.id = editItem.value.id || saved.data.id
    emit('update', editItem.value)
  } catch(error)  {
    if (!documentSaved) {
      await resolvedContact?.rollback()
      await rollbackCompany(createdCompanyId)
      editItem.value.company = typedCompany
      editItem.value.contact = typedContact
    }
    console.error(error)
    notifyError(i18n.t('error.saveFailed'))
  }
}

const onIsEstimateChange = (newVal) => {
  if (editItem.value.projects?.length > 0) {
    Dialog.create({
      message: i18n.t('warning.changeEstimateFlagLinkedToProject'),
      cancel: true,
      persistent: true,
    }).onOk(() => {
      editItem.value.is_estimate = newVal
    })
  } else {
    editItem.value.is_estimate = newVal
  }
}

const { companyReadonly, setCompany, applyListingCompany } = useListingCompany(editItem)

const onListingChanged = (listingId) => {
  editItem.value.listing = listingId
  applyListingCompany()
}

const { deleteOptions, onDeleteOptionSelect, deleteAsync } = useQuoteDeleteOptions({
  onDeleted: () => emit('delete'),
})

const showDeleteDialog = ref(false)

const onConfirmDelete = () => { showDeleteDialog.value = true }

const onDeleteConfirmed = () => deleteAsync(editItem.value)

const form = ref(null)

const refreshTermTemplates = () => Promise.all([
  accountingStore.fetchPaymentTermTemplatesAsync({}, true),
  accountingStore.fetchBudgetEstimateTermTemplatesAsync({}, true),
  accountingStore.fetchIntellectualPropertyTermTemplatesAsync({}, true),
  accountingStore.fetchPaymentCoordinatesTemplatesAsync({}, true),
])

const termTitles = ref({})

const termFieldByType = {
  [accountingStore.documentTermTemplateTypeEnum.PAYMENT_TERMS]: 'payment_terms',
  [accountingStore.documentTermTemplateTypeEnum.PAYMENT_COORDINATES]: 'payment_coordinates',
  [accountingStore.documentTermTemplateTypeEnum.BUDGET_ESTIMATE_TERMS]: 'budget_estimate_terms',
  [accountingStore.documentTermTemplateTypeEnum.INTELLECTUAL_PROPERTY_TERMS]: 'intellectual_property_terms',
}
const templatesByType = {
  [accountingStore.documentTermTemplateTypeEnum.PAYMENT_TERMS]: () => accountingStore.paymentTermTemplates,
  [accountingStore.documentTermTemplateTypeEnum.PAYMENT_COORDINATES]: () => accountingStore.paymentCoordinatesTemplates,
  [accountingStore.documentTermTemplateTypeEnum.BUDGET_ESTIMATE_TERMS]: () => accountingStore.budgetEstimateTermTemplates,
  [accountingStore.documentTermTemplateTypeEnum.INTELLECTUAL_PROPERTY_TERMS]: () => accountingStore.intellectualPropertyTermTemplates,
}

const pendingTemplateSyncs = () => {
  const pending = []
  for (const [termType, field] of Object.entries(termFieldByType)) {
    const title = (termTitles.value[termType] || '').trim()
    const description = editItem.value[field]
    if (!title || !description?.trim()) continue
    const match = templatesByType[termType]().find(t => (t.title || '').trim().toLowerCase() === title.toLowerCase())
    if (match && description === (match.description || '')) continue
    pending.push({ title, description, term_type: Number(termType) })
  }
  return pending
}

const promptTemplateSyncThen = (proceed) => {
  const pending = pendingTemplateSyncs()
  if (!pending.length) { proceed(); return }
  Dialog.create({
    title: i18n.t('common.documentTemplates', 2),
    message: i18n.t('budgetDocument.edit.updateTemplatesQuestion'),
    ok: { label: i18n.t('common.save'), color: 'positive' },
    cancel: { label: i18n.t('common.no'), flat: true },
    persistent: true,
  })
    .onOk(async () => {
      await Promise.all(pending.map(p => accountingStore.upsertDocumentTermTemplateByTitleAsync(p)))
      await refreshTermTemplates()
      proceed()
    })
    .onCancel(() => proceed())
}

const onUpdateWithTemplatePrompt = () => promptTemplateSyncThen(onUpdate)

onMounted(async () => {
  if (editItem.value.id) {
    const { data } = await accountingStore.getBudgetDocumentSchedulesAsync(editItem.value.id)
    if (data?.length) {
      editItem.value.schedules = data.map(s => ({
        start_time: s.start_time?.substring(0, 5) || s.start_time,
        end_time: s.end_time?.substring(0, 5) || s.end_time,
      }))
    }
    applyListingCompany()
    setOriginal(editItem.value)
    Object.assign(editItem.value, props.changes)
  } else if (editItem.value.listing) {
    applyListingCompany()
  }
  try {
    await Promise.all([
      accountingStore.fetchBudgetTagsAsync({}, true),
      accountingStore.fetchPaymentTermTemplatesAsync({}, true),
      accountingStore.fetchBudgetEstimateTermTemplatesAsync({}, true),
      accountingStore.fetchIntellectualPropertyTermTemplatesAsync({}, true),
      accountingStore.fetchPaymentCoordinatesTemplatesAsync({}, true),
    ])
    availableBudgetDocumentTags.value = accountingStore.getLocalizedBudgetTags()
  } catch (error) {
    console.error('Failed to load term templates', error)
  }
  if (!editItem.value.id && !editItem.value.listing) {
    const genericListing = planningStore.listings.find(l => l.is_generic)
    if (genericListing) editItem.value.listing = genericListing.id
  }
})

const emit = defineEmits(['update', 'cancel', 'delete'])
useNavigator(() => emit('cancel'))
useEscapeClose(isUpdated, () => emit('cancel'))
const onCancel = () => emit('cancel')

</script>
<template>
  <q-card class="dialog-card-flex">
    <tag-create-dialog
      :draft="tagDraft"
      :kinds="accountingStore.getLocalizedBudgetTagKinds()"
      :location-kind="accountingStore.budgetTagKindEnum.LOCATION"
      @confirm="confirmTagDraftAsync"
      @cancel="tagDraft = null"
    />
    <navigate-back
      :text="editItem?.id ? i18n.t('common.editBudgetDocument') : i18n.t('common.addBudgetDocument')"
      @cancel="onCancel"
    >
      <template v-if="!editItem?.id" #right>
        <create-mode-toggle v-model="quickCreate" />
      </template>
    </navigate-back>
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll" :class="{ 'dialog-card-content': editItem?.id }">
      <q-card-section class="q-px-sm">
        <q-form ref="form">
          <q-stepper
            v-if="quickCreate"
            v-model="quickStep"
            class="q-py-none"
          >
            <q-step
              :name="1"
              :title="i18n.t('common.quickCreate')"
              icon="mdi-flash"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.name"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.name')"
                  autofocus
                  :rules="[val => val && val.length > 0 || i18n.t('common.required')]"
                />
                <ListingSelect
                  :model-value="editItem.listing"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  @update:model-value="onListingChanged"
                />
                <currency-select
                  :model-value="editItem.currency_ref"
                  :hint="referenceCurrencyHint(editItem.currency_ref)"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  @update:model-value="onCurrencyChange"
                />
                <print-template-select
                  v-model="editItem.print_template"
                  :document-type="accountingStore.printDocumentTypeEnum.QUOTE"
                  :class="isMobile ? 'col-12' : 'col-3'"
                />
                <exchange-rate-panel
                  :model-value="pickExchangeRate(editItem)"
                  :currency-ref="editItem.currency_ref"
                  class="col-12"
                  @update:model-value="onExchangeRateChange"
                />
                <form-section-title :label="i18n.t('common.customer')" />
                <company-combobox
                  :model-value="editItem.company"
                  :readonly="companyReadonly"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  @update:model-value="setCompany"
                />
                <contact-select
                  v-model="editItem.contact"
                  :company-id="editItem.company"
                  :disable="!editItem.company"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  @load-references="loadContactReferences"
                />
                <contact-reference-input
                  v-model="contactReferences[planningStore.referenceTypeEnum.PHONE]"
                  :contact="editItem.contact"
                  :reference-type="planningStore.referenceTypeEnum.PHONE"
                  :class="isMobile ? 'col-12' : 'col-3'"
                />
                <contact-reference-input
                  v-model="contactReferences[planningStore.referenceTypeEnum.EMAIL]"
                  :contact="editItem.contact"
                  :reference-type="planningStore.referenceTypeEnum.EMAIL"
                  :class="isMobile ? 'col-12' : 'col-3'"
                />
                <form-section-title :label="i18n.t('common.period')" />
                <div class="col-12">
                  <date-time-range-step
                    v-model="editItem"
                    :schedules="editItem.schedules"
                    @update:schedules="editItem.schedules = $event"
                  />
                </div>
                <q-toggle
                  :model-value="editItem.is_estimate"
                  class="col-12"
                  :label="i18n.t('common.isEstimate')"
                  color="primary"
                  @update:model-value="onIsEstimateChange"
                />
              </div>
              <stepper-navigation
                :finish="() => form.validate().then(success => success && onUpdateWithTemplatePrompt())"
                :finish-label="i18n.t('common.addBudgetDocument')"
              />
            </q-step>
          </q-stepper>
          <q-stepper
            v-else
            v-model="step"
            class="q-py-none"
            vertical
          >
            <q-step
              :name="1"
              :title="i18n.t('common.basicInfo')"
              icon="mdi-folder-plus"
              :done="!!editItem.id || step > 1"
              :header-nav="!!editItem.id || step > 1"
              :error="wasAttempted(1) && basicInfoHasError"
              error-color="negative"
              error-icon="mdi-alert-circle"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.name"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.name')"
                  autofocus
                  :rules="[val => val && val.length > 0 || i18n.t('common.required')]"
                />
                <ListingSelect
                  :model-value="editItem.listing"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  @update:model-value="onListingChanged"
                />
                <currency-select
                  :model-value="editItem.currency_ref"
                  :hint="referenceCurrencyHint(editItem.currency_ref)"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  @update:model-value="onCurrencyChange"
                />
                <q-input
                  v-model="editItem.request_reference"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.requestReference')"
                />
                <exchange-rate-panel
                  :model-value="pickExchangeRate(editItem)"
                  :currency-ref="editItem.currency_ref"
                  class="col-12"
                  @update:model-value="onExchangeRateChange"
                />
                <form-section-title :label="i18n.t('common.customer')" />
                <company-combobox
                  :model-value="editItem.company"
                  :readonly="companyReadonly"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  @update:model-value="setCompany"
                />
                <contact-select
                  v-model="editItem.contact"
                  :company-id="editItem.company"
                  :disable="!editItem.company"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  @load-references="loadContactReferences"
                />
                <contact-reference-input
                  v-model="contactReferences[planningStore.referenceTypeEnum.PHONE]"
                  :contact="editItem.contact"
                  :reference-type="planningStore.referenceTypeEnum.PHONE"
                  :class="isMobile ? 'col-12' : 'col-3'"
                />
                <contact-reference-input
                  v-model="contactReferences[planningStore.referenceTypeEnum.EMAIL]"
                  :contact="editItem.contact"
                  :reference-type="planningStore.referenceTypeEnum.EMAIL"
                  :class="isMobile ? 'col-12' : 'col-3'"
                />
                <form-section-title :label="i18n.t('common.details')" />
                <q-input
                  v-model="editItem.description"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.description')"
                  autogrow
                />
                <print-template-select
                  v-model="editItem.print_template"
                  :document-type="accountingStore.printDocumentTypeEnum.QUOTE"
                  :class="isMobile ? 'col-12' : 'col-3'"
                />
                <document-language-select
                  v-model="editItem.language"
                  :class="isMobile ? 'col-12' : 'col-3'"
                />
                <q-select
                  v-model="editItem.tags"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.tag', 2)"
                  multiple
                  use-chips
                  use-input
                  emit-value
                  map-options
                  option-value="id"
                  option-label="name"
                  :options="availableBudgetDocumentTags"
                  new-value-mode="add-unique"
                  @filter="onBudgetDocumentTagFilter"
                  @new-value="onCreateBudgetDocumentTag"
                >
                  <template #selected-item="chip">
                    <q-chip
                      removable
                      size="sm"
                      :tabindex="chip.tabindex"
                      class="q-ma-none"
                      :color="chip.color ? chip.color : 'initial'"
                      :style="{ 'background-color': stringToColor(chip.opt.name), 'color': textColor(stringToColor(chip.opt.name)), 'margin': 0, 'margin-right': '.25rem' }"
                      @remove="chip.removeAtIndex(chip.index)"
                    >
                      {{ chip.opt.name }}
                    </q-chip>
                  </template>
                </q-select>
                <q-toggle
                  :model-value="editItem.is_estimate"
                  class="col-12"
                  :label="i18n.t('common.isEstimate')"
                  color="primary"
                  @update:model-value="onIsEstimateChange"
                />
              </div>
              <stepper-navigation
                :continue="() => { markAttempted(1); form.validate().then((success) => (success ? nextStep() : null)) }"
              />
            </q-step>
            <q-step
              :name="2"
              :title="i18n.t('project.edit.addDateAndTime')"
              icon="mdi-calendar-clock"
              :done="!!editItem.id || step > 2"
              :header-nav="!!editItem.id || step > 2"
            >
              <date-time-range-step
                v-model="editItem"
                :schedules="editItem.schedules"
                :continue-action="() => { markAttempted(2); form.validate().then((success) => (success ? nextStep() : null)) }"
                :back-action="prevStep"
                @update:schedules="editItem.schedules = $event"
              />
            </q-step>
            <q-step
              :name="3"
              :title="i18n.t('common.payment', 2)"
              icon="mdi-currency-usd"
              :done="!!editItem.id || step > 3"
              :header-nav="!!editItem.id || step > 3"
            >
              <div class="row q-col-gutter-sm">
                <document-term-template-select
                  v-model:template-title="termTitles[accountingStore.documentTermTemplateTypeEnum.PAYMENT_TERMS]"
                  v-model="editItem.payment_terms"
                  :label="i18n.t('common.paymentTerms')"
                  :template-label="i18n.t('common.paymentTermTemplate')"
                  :term-type="accountingStore.documentTermTemplateTypeEnum.PAYMENT_TERMS"
                  :templates="accountingStore.paymentTermTemplates"
                  class="col-12"
                  @refresh="refreshTermTemplates"
                />
                <document-term-template-select
                  v-model:template-title="termTitles[accountingStore.documentTermTemplateTypeEnum.PAYMENT_COORDINATES]"
                  v-model="editItem.payment_coordinates"
                  :label="i18n.t('common.paymentCoordinates')"
                  :template-label="i18n.t('common.paymentCoordinatesTemplate')"
                  :term-type="accountingStore.documentTermTemplateTypeEnum.PAYMENT_COORDINATES"
                  :templates="accountingStore.paymentCoordinatesTemplates"
                  class="col-12"
                  @refresh="refreshTermTemplates"
                />
              </div>
              <stepper-navigation
                :continue="nextStep"
                :back="prevStep"
              />
            </q-step>
            <q-step
              :name="4"
              :title="i18n.t('common.additionalInfo', 2)"
              icon="mdi-information-outline"
              :done="!!editItem.id || step > 3"
              :header-nav="!!editItem.id || step > 3"
            >
              <div class="row q-col-gutter-sm">
                <document-term-template-select
                  v-model:template-title="termTitles[accountingStore.documentTermTemplateTypeEnum.BUDGET_ESTIMATE_TERMS]"
                  v-model="editItem.budget_estimate_terms"
                  :label="i18n.t('common.budgetEstimateTerms')"
                  :template-label="i18n.t('common.budgetEstimateTermTemplate')"
                  :term-type="accountingStore.documentTermTemplateTypeEnum.BUDGET_ESTIMATE_TERMS"
                  :templates="accountingStore.budgetEstimateTermTemplates"
                  class="col-12"
                  @refresh="refreshTermTemplates"
                />
                <document-term-template-select
                  v-model:template-title="termTitles[accountingStore.documentTermTemplateTypeEnum.INTELLECTUAL_PROPERTY_TERMS]"
                  v-model="editItem.intellectual_property_terms"
                  :label="i18n.t('common.intellectualPropertyTerms')"
                  :template-label="i18n.t('common.intellectualPropertyTermTemplate')"
                  :term-type="accountingStore.documentTermTemplateTypeEnum.INTELLECTUAL_PROPERTY_TERMS"
                  :templates="accountingStore.intellectualPropertyTermTemplates"
                  class="col-12"
                  @refresh="refreshTermTemplates"
                />
                <q-input
                  v-model="editItem.notes"
                  autogrow
                  class="col-12"
                  :label="i18n.t('common.note', 2)"
                />
              </div>
              <stepper-navigation
                :back="prevStep"
                :finish="editItem.id ? null : onUpdateWithTemplatePrompt"
                :finish-label="i18n.t('common.addBudgetDocument')"
              />
            </q-step>
          </q-stepper>
        </q-form>
      </q-card-section>
    </q-card-section>
    <q-card-actions v-if="editItem?.id" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn q-mr-sm"
        color="negative"
        :label="i18n.t('common.delete')"
        @click="onConfirmDelete()"
      />
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :disable="editItem.name === ''"
        :label="i18n.t('common.update')"
        @click="onUpdateWithTemplatePrompt"
      />
    </q-card-actions>
    <delete-options-dialog
      v-model="deleteOptions.show"
      :title="deleteOptions.title"
      :message="deleteOptions.message"
      :caption="deleteOptions.caption"
      :options="deleteOptions.options"
      @select="onDeleteOptionSelect"
    />
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDeleteConfirmed"
    />
  </q-card>
</template>
<style scoped>
</style>
